import { createContext, useContext, type ReactNode } from 'react';

/**
 * V1.2 RBAC — client-side permission gate. Wrap any component in
 * `<Guard viewId="view:admin.billing">` to hide it when the current user
 * lacks the permission. `fallback` is rendered in place of the guarded
 * children (defaults to `null` — the component simply disappears).
 *
 * The server enforces the same gate via `authorize(viewId)` middleware;
 * this component is only UX (hide nav links the user can't use). Security
 * gains come from the server check.
 *
 * Usage:
 *   <PermissionsProvider permissions={me.permissions}>
 *     …
 *     <Guard viewId="view:admin.billing"><NavItem …/></Guard>
 */
interface PermissionsState {
  permissions: readonly string[];
}

const PermissionsCtx = createContext<PermissionsState | null>(null);

export function PermissionsProvider({
  permissions,
  children,
}: {
  permissions: readonly string[];
  children: ReactNode;
}) {
  return <PermissionsCtx.Provider value={{ permissions }}>{children}</PermissionsCtx.Provider>;
}

export function useUserPermissions(): readonly string[] {
  const ctx = useContext(PermissionsCtx);
  return ctx?.permissions ?? [];
}

export function hasPermission(permissions: readonly string[], viewId: string): boolean {
  if (permissions.includes('*')) return true;
  return permissions.includes(viewId);
}

export function Guard({
  viewId,
  children,
  fallback = null,
}: {
  viewId: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const perms = useUserPermissions();
  return <>{hasPermission(perms, viewId) ? children : fallback}</>;
}
